/**
 * Force color option values to render as solid swatches (not text pills).
 * Targets Globo + native pickers. Re-applies against Globo re-renders.
 */
(function () {
  'use strict';

  const COLOR_MAP = {
    black: '#0a0a0a',
    white: '#ffffff',
    ivory: '#fffff0',
    cream: '#f5f0e1',
    beige: '#f5f5dc',
    gray: '#808080',
    grey: '#808080',
    charcoal: '#36454f',
    navy: '#001f3f',
    blue: '#2563eb',
    red: '#b91c1c',
    burgundy: '#6b1c23',
    brown: '#7b4a2d',
    tan: '#d2b48c',
    khaki: '#c3b091',
    olive: '#556b2f',
    green: '#2f6b3a',
    'light green': '#9bcf8a',
    lightgreen: '#9bcf8a',
    'light-green': '#9bcf8a',
    light: '#d8e8d0',
    mint: '#98d7c2',
    teal: '#008080',
    yellow: '#eab308',
    gold: '#a8864c',
    orange: '#ea580c',
    pink: '#ec4899',
    purple: '#7c3aed',
    violet: '#8b5cf6',
    'green-ophite': '#6f7f6a',
    'green ophite': '#6f7f6a',
    ophite: '#6f7f6a',
    'grey-green': '#7a8a78',
    'gray-green': '#7a8a78',
    offwhite: '#f7f4ef',
    'off white': '#f7f4ef',
    'off-white': '#f7f4ef',
    bone: '#e8e0d5',
    sand: '#c2b280',
    'army green': '#4b5320',
    forest: '#228b22',
    sage: '#9caf88'
  };

  const SIZE_RE = /^(xxs|xs|s|m|l|xl|xxl|2xs|2xl|3xl|4xl|age\s*\d+)$/i;
  const COLOR_HINT = /color|colour|צבע|couleur|colore|farbe/i;

  function normalize(value) {
    return String(value || '')
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function resolveColor(name) {
    const key = normalize(name);
    if (!key || SIZE_RE.test(key)) return null;
    if (COLOR_MAP[key]) return COLOR_MAP[key];
    if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(key)) return key;

    const compact = key.replace(/[\s/_-]+/g, '');
    if (COLOR_MAP[compact]) return COLOR_MAP[compact];

    // Prefer multi-word matches first ("light green" before "green")
    const parts = key.split(/[\s/_-]+/).filter(Boolean);
    for (let len = parts.length; len >= 1; len--) {
      for (let start = 0; start <= parts.length - len; start++) {
        const slice = parts.slice(start, start + len).join(' ');
        if (COLOR_MAP[slice]) return COLOR_MAP[slice];
        const joined = parts.slice(start, start + len).join('');
        if (COLOR_MAP[joined]) return COLOR_MAP[joined];
      }
    }

    return null;
  }

  function optionText(el) {
    const attrs = [
      el.getAttribute('data-value'),
      el.getAttribute('data-option-value'),
      el.getAttribute('data-name'),
      el.getAttribute('data-swatch'),
      el.getAttribute('title'),
      el.getAttribute('aria-label'),
      el.value
    ]
      .map((v) => (v || '').trim())
      .filter(Boolean);

    for (let i = 0; i < attrs.length; i++) {
      if (resolveColor(attrs[i])) return attrs[i];
    }

    // Prefer a single short text node (avoids "ColorLight green" concatenations)
    const own = Array.from(el.childNodes)
      .filter((n) => n.nodeType === 3)
      .map((n) => n.textContent.trim())
      .filter(Boolean)
      .join(' ');
    if (own && resolveColor(own)) return own;

    const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
    if (attrs[0]) return attrs[0];
    return text;
  }

  function hasNativeColorFill(el) {
    if (!el) return false;
    if (el.classList.contains('globo-border-color-swatch')) return true;
    if (el.querySelector?.('.globo-border-color-swatch')) return true;

    const nodes = [el];
    if (el.querySelectorAll) {
      el.querySelectorAll('label, a, button, span, div').forEach((n) => nodes.push(n));
    }

    return nodes.some((node) => {
      const style = node.getAttribute?.('style') || '';
      if (/background-image\s*:\s*url\(/i.test(style)) return true;
      if (/background(-color)?\s*:\s*(#|rgb|hsl)/i.test(style)) return true;
      // Globo often sets background via class without "swatch-other"
      if (
        node.classList?.contains('globo-border-color-swatch') ||
        /color-swatch|swatch_color/i.test(node.className || '')
      ) {
        return true;
      }
      return false;
    });
  }

  function needsTextColorPaint(el) {
    if (!el || hasNativeColorFill(el)) return false;
    if (SIZE_RE.test(normalize(optionText(el)))) return false;
    // Only convert text pills ("Light green") — leave real Globo color buttons alone
    if (el.classList.contains('swatch-other')) return true;
    if (el.classList.contains('globo-style--button') && !hasNativeColorFill(el)) {
      return isKnownColorName(optionText(el));
    }
    return false;
  }

  function paintSizeFor(el) {
    if (
      el.closest(
        '[data-613-product-card], .product-card-613, .product-card-613-wrapper, .card-wrapper, .card--card, .grid__item .swatches-globo'
      )
    ) {
      return '2rem';
    }
    return '3.2rem';
  }

  function paint(el, hex, label) {
    // Never paint the list item wrapper — Globo binds clicks there
    if (el.tagName === 'LI') return;
    if (hasNativeColorFill(el)) return;

    el.classList.add('is-613-color-swatch');
    el.setAttribute('data-613-color-swatch', 'true');
    el.setAttribute('data-613-swatch-color', hex);
    el.style.setProperty('--613-swatch', hex);
    if (label) {
      el.setAttribute('title', label);
      el.setAttribute('aria-label', label);
    }

    const size = paintSizeFor(el);

    const styles = {
      background: hex,
      'background-color': hex,
      'background-image': 'none',
      color: 'transparent',
      '-webkit-text-fill-color': 'transparent',
      'font-size': '0px',
      'line-height': '0',
      'text-indent': '-9999px',
      width: size,
      'min-width': size,
      'max-width': size,
      height: size,
      'min-height': size,
      padding: '0',
      margin: '0',
      overflow: 'hidden',
      display: 'inline-block',
      'vertical-align': 'middle',
      'border-radius': '2px',
      border: '1px solid #ffffff',
      'box-sizing': 'border-box',
      'pointer-events': 'auto',
      cursor: 'pointer',
      'box-shadow':
        hex === '#ffffff' || hex === '#fffff0' || hex === '#f7f4ef' || hex === '#f5f0e1'
          ? 'inset 0 0 0 1px rgba(0,0,0,0.2)'
          : 'none'
    };

    Object.keys(styles).forEach((prop) => {
      el.style.setProperty(prop, styles[prop], 'important');
    });
  }

  function isOnProductCard(el) {
    return Boolean(
      el.closest(
        '[data-613-product-card], .product-card-613, .product-card-613-wrapper, .card-wrapper'
      ) || (el.closest('.grid__item') && el.closest('.swatches-globo') && !el.closest('.product__info-container'))
    );
  }

  function isColorContext(el) {
    if (el.closest('.g-variant-color, [class*="g-variant-color"]')) return true;

    // Collection cards: only treat g-variant-color as color context
    if (isOnProductCard(el)) return false;

    const wrap = el.closest(
      '.swatch--gl, .swatches-globo, .globo-swatch-list, .globo-swatch-product-detail, fieldset.product-form__input'
    );
    if (!wrap) return false;

    const title = wrap.querySelector(
      '.swatch-globo-title, .globo-title, .name-option, legend, .form__label, .label, [class*="title"]'
    );
    if (title && COLOR_HINT.test(title.textContent || '')) return true;

    const prev = wrap.previousElementSibling;
    if (prev && COLOR_HINT.test(prev.textContent || '')) return true;

    const heading = wrap.parentElement?.querySelector('p, span, div, label');
    if (heading && COLOR_HINT.test(heading.textContent || '') && /color|צבע/i.test(heading.textContent)) {
      return true;
    }

    return el.classList.contains('swatch_color_prd') || /color_/i.test(el.className || '');
  }

  function isKnownColorName(label) {
    const key = normalize(label);
    if (!key || SIZE_RE.test(key)) return false;
    if (COLOR_MAP[key] || COLOR_MAP[key.replace(/[\s/_-]+/g, '')]) return true;
    return !!resolveColor(label);
  }

  function clearWrongPaint(el, { keepBackground = false } = {}) {
    if (!el) return;
    el.classList.remove('is-613-color-swatch');
    el.removeAttribute('data-613-color-swatch');
    el.removeAttribute('data-613-swatch-color');
    el.style.removeProperty('--613-swatch');

    const props = [
      'color',
      '-webkit-text-fill-color',
      'font-size',
      'line-height',
      'text-indent',
      'width',
      'min-width',
      'max-width',
      'height',
      'min-height',
      'padding',
      'margin',
      'overflow',
      'display',
      'vertical-align',
      'border-radius',
      'border',
      'box-sizing',
      'box-shadow',
      'pointer-events',
      'cursor'
    ];

    if (!keepBackground) {
      props.push('background', 'background-color', 'background-image');
    }

    props.forEach((prop) => el.style.removeProperty(prop));
  }

  function tryPaint(el) {
    const label = optionText(el);
    const hex = resolveColor(label);
    if (!hex) return false;
    if (SIZE_RE.test(normalize(label))) return false;
    if (!needsTextColorPaint(el)) return false;

    // On cards: only paint text-only colors inside Globo color lists
    if (isOnProductCard(el) && !el.closest('.g-variant-color')) return false;

    if (!isColorContext(el) && !el.classList.contains('swatch_color_prd')) {
      if (!isKnownColorName(label)) return false;
    }
    paint(el, hex, label);
    return true;
  }

  function run() {
    // Undo paints that break Globo native color swatches or size pills
    document.querySelectorAll('[data-613-color-swatch="true"], .is-613-color-swatch').forEach((el) => {
      if (el.tagName === 'LI') {
        clearWrongPaint(el);
        return;
      }
      if (hasNativeColorFill(el)) {
        clearWrongPaint(el, { keepBackground: true });
        return;
      }
      const list = el.closest('ul, .ul-swatches-list');
      if (list && sizeCountLike(list) && !list.classList.contains('g-variant-color')) {
        clearWrongPaint(el);
        return;
      }
      if (isOnProductCard(el) && !el.closest('.g-variant-color')) {
        clearWrongPaint(el);
      }
    });

    // Strip layout overrides from LI wrappers that blocked Globo clicks
    document.querySelectorAll('.product__info-container .swatches-globo li.is-613-color-swatch, .product__info-container .swatches-globo li[data-613-color-swatch]').forEach((li) => {
      clearWrongPaint(li);
    });

    // Restore pointer events on color option items
    document
      .querySelectorAll('.product__info-container .swatches-globo li')
      .forEach((li) => {
        if (!isColorContext(li) && !li.closest('.g-variant-color') && !hasNativeColorFill(li)) return;
        li.style.removeProperty('pointer-events');
        li.querySelectorAll('label, a, button, span, input, .globo-border-color-swatch').forEach((child) => {
          child.style.removeProperty('pointer-events');
          if (child.style.cursor === 'pointer') child.style.removeProperty('cursor');
        });
        li.style.setProperty('pointer-events', 'auto', 'important');
        const clickable = li.querySelector('label, a, button, .globo-border-color-swatch, input');
        if (clickable) {
          clickable.style.setProperty('pointer-events', 'auto', 'important');
          clickable.style.setProperty('cursor', 'pointer', 'important');
        }
      });

    // 1) Native theme color labels (product page) — only fill empty ones
    document.querySelectorAll('label.swatch_color_prd, label[class*="color_"]').forEach((el) => {
      if (isOnProductCard(el)) return;
      if (hasNativeColorFill(el) && /background(-color)?\s*:\s*(#|rgb)/i.test(el.getAttribute('style') || '')) {
        return;
      }
      const hex = resolveColor(optionText(el));
      if (hex) paint(el, hex, optionText(el));
    });

    // 2) Globo text-only color buttons ("Light green") — never overwrite real swatches
    document
      .querySelectorAll(
        '.product__info-container .swatches-globo .g-variant-color li, .product__info-container .swatches-globo li'
      )
      .forEach((li) => {
        if (!isColorContext(li) && !li.closest('.g-variant-color')) return;
        if (hasNativeColorFill(li)) return;

        const value =
          li.getAttribute('data-value') ||
          li.getAttribute('data-option-value') ||
          optionText(li);
        const hex = resolveColor(value);
        if (!hex) return;

        const list = li.closest('ul, .ul-swatches-list');
        if (list && sizeCountLike(list) && !list.classList.contains('g-variant-color')) return;

        const target =
          li.querySelector('label.swatch-other, a.swatch-other, label.globo-style--button, a.globo-style--button, label, a, button') ||
          null;
        if (!target || !needsTextColorPaint(target)) return;
        paint(target, hex, value.trim());
      });

    // 3) Direct text color buttons on product page
    document
      .querySelectorAll(
        '.product__info-container .swatches-globo .globo-style--button.swatch-other, .product__info-container label.swatch-anchor.swatch-other'
      )
      .forEach((el) => {
        tryPaint(el);
      });
  }

  function sizeCountLike(list) {
    if (!list) return false;
    const samples = Array.from(list.querySelectorAll('li'))
      .slice(0, 5)
      .map((item) => normalize(optionText(item)));
    return samples.filter((s) => SIZE_RE.test(s)).length >= 2;
  }

  let scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      run();
    });
  }

  function init() {
    run();
    const root = document.body;
    if (root) {
      new MutationObserver(schedule).observe(root, {
        childList: true,
        subtree: true
      });
    }
    [50, 150, 400, 800, 1500, 3000].forEach((ms) => window.setTimeout(run, ms));
    document.addEventListener('click', () => window.setTimeout(run, 80), true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
